import { TaskView } from '@nexelem/graphne-api';
import { useMemo } from 'react';
import { ProductionOrderItem } from './ProductionOrderItem';
import { SalesOrderItem } from './SalesOrderItem';
import { ProductionLotItem } from './ProductionLot';
import { useTaskDependencies, useProductionOrderDetails, useOrdersDetails } from '../../../../../hooks';
import { Skeleton } from '@nexelem/components';
import { useIsGanttSimulation } from '../../../../../../../../components/organisms/ProductionPlans/GanttSimulation/hooks/GanttSimulationContext';

type ConnectionsTabProps = {
  task: TaskView;
  isGraphDrawer: boolean;
  isKanban: boolean;
};

export const ConnectionsTab = ({ task, isGraphDrawer, isKanban }: ConnectionsTabProps) => {
  const { orders, productionOrderDetails, taskType } = task;
  const isSimulation = useIsGanttSimulation();
  // For simulation, productionOrderDetails always has id=1 from backend,
  // so we skip the API call as data is already available
  const { data: productionOrderDetailsData, isLoading: isProductionOrderLoading } = useProductionOrderDetails(
    isSimulation ? undefined : productionOrderDetails?.id,
  );
  const { previousTasks, nextTasks, isLoading: isTaskDependenciesLoading } = useTaskDependencies(task.id);

  const orderIds = useMemo(() => orders?.map((order) => order.orderId) || [], [orders]);
  const { data: ordersDetails, isLoading: isOrdersLoading } = useOrdersDetails(orderIds);

  const buyerNames = useMemo(() => {
    if (!orders) {
      return {};
    }
    return Object.fromEntries(
      orders.map((order) => [order.orderId, order.buyerName]).filter(([_, name]) => name !== undefined),
    );
  }, [orders]);

  const isLoading = isProductionOrderLoading || isOrdersLoading || isTaskDependenciesLoading;
  const productionOrderData = productionOrderDetailsData || productionOrderDetails;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton className="h-20" key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col py-2">
      <ProductionOrderItem
        dueDate={productionOrderDetailsData?.dueDate}
        externalId={productionOrderDetails?.externalId}
        id={productionOrderData?.id}
        number={productionOrderData?.number}
        status={productionOrderDetailsData?.status}
        taskType={taskType}
      />
      <SalesOrderItem buyerName={buyerNames} data={ordersDetails} />
      <ProductionLotItem
        isGraphDrawer={isGraphDrawer}
        isKanban={isKanban}
        nextTasks={nextTasks}
        previousTasks={previousTasks}
      />
    </div>
  );
};
